// src/pages/ia-paca/metiers/ml-engineer.tsx
//
// Page métier — Machine Learning Engineer en PACA.
//
// Stratégie SEO : capter "recrutement ML Engineer", "machine learning
// engineer Marseille", "ml engineer Sophia", "salaire ML engineer 2026",
// "différence ML engineer / AI Engineer / Data Scientist".

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../../components/layout/layout';
import IaPacaHero from '../../../components/ia-paca/IaPacaHero';
import IaPacaCTA from '../../../components/ia-paca/IaPacaCTA';
import IaSalaryTable from '../../../components/ia-paca/IaSalaryTable';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Code2,
  Cpu,
  GitBranch,
  Brain,
  TrendingUp,
} from 'lucide-react';

export default function MlEngineerPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Occupation',
    name: 'Machine Learning Engineer',
    description:
      "Le Machine Learning Engineer industrialise les modèles ML : feature engineering, entraînement à l'échelle, déploiement, monitoring. Pont entre Data Science et Engineering, avec un focus modèles classiques ET deep learning (vs LLM-first pour l'AI Engineer).",
    occupationLocation: [
      { '@type': 'City', name: 'Marseille' },
      { '@type': 'City', name: 'Sophia Antipolis' },
      { '@type': 'City', name: 'Aix-en-Provence' },
      { '@type': 'City', name: 'Nice' },
      { '@type': 'AdministrativeArea', name: "Provence-Alpes-Côte d'Azur" },
    ],
    estimatedSalary: [
      {
        '@type': 'MonetaryAmountDistribution',
        currency: 'EUR',
        duration: 'P1Y',
        percentile10: 42000,
        median: 72000,
        percentile90: 100000,
      },
    ],
    skills:
      'Python, scikit-learn, PyTorch, TensorFlow, XGBoost, MLflow, Kubeflow, Airflow, Docker, Kubernetes, AWS SageMaker / GCP Vertex AI / Azure ML, feature store (Feast), monitoring (Evidently, Arize)',
    educationRequirements:
      "Master en informatique, mathématiques appliquées, IA, ou ingénieur grande école avec expérience ML.",
    experienceRequirements: 'Junior (0-2 ans) à Senior (8+ ans).',
  };

  return (
    <Layout
      title="Recrutement Machine Learning Engineer en PACA — Marseille, Sophia | recruteurs.io"
      description="Recruter un ML Engineer en PACA : profils PyTorch, MLflow, SageMaker, Vertex AI. Cabinet de recrutement spécialisé qui maîtrise la stack MLOps moderne. Salaire 42-100k€. Court-list en 15-20 jours."
      keywords="recrutement Machine Learning Engineer, ML Engineer Marseille, ML Engineer Sophia Antipolis, salaire ML engineer 2026, recruter ingénieur machine learning PACA, ML Engineer Nice, embaucher ML Engineer"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'ML Engineer', href: '/ia-paca/metiers/ml-engineer' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <IaPacaHero
        eyebrow="Métier IA · PACA"
        title="Recruter un"
        highlight="Machine Learning Engineer"
        subtitle="Pas un Data Scientist 'qui sait coder un peu'. Pas un Data Engineer 'qui a vu PyTorch'. Un ingénieur qui prend un modèle ML, le met en production, le monitore, le réentraîne, et le maintient sur la durée."
        badges={['MLflow', 'PyTorch', 'SageMaker', 'Feature Store', 'Monitoring']}
      />

      <div className="bg-white pt-6">
        <div className="mx-auto max-w-4xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Tous les métiers IA
          </Link>
        </div>
      </div>

      {/* C'est quoi vraiment */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Le métier — sans bullshit
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Un ML Engineer, c'est quoi vraiment en 2026 ?
          </h2>
          <div className="prose prose-slate mt-6 max-w-none text-base leading-relaxed text-slate-700">
            <p>
              Le ML Engineer transforme un modèle qui marche sur un notebook
              Jupyter en un système qui sert <strong>des prédictions à
              l'échelle, de façon fiable, monitorée et maintenable</strong>.
            </p>
            <p>
              Concrètement, sur une journée type : il itère sur du feature
              engineering, lance des runs MLflow ou Weights & Biases, package
              un modèle dans un container, l'expose via une API, met en place
              du monitoring de drift et de performance, et débugge le pipeline
              de training/inference quand quelque chose part en cacahuète.
            </p>
            <p>
              <strong>Différence avec l'AI Engineer :</strong> l'AI Engineer
              est LLM-first (RAG, agents, prompt engineering). Le ML Engineer
              couvre un spectre plus large : forecasting, scoring, recsys,
              computer vision, NLP classique, et oui aussi LLM quand le cas
              d'usage le justifie. Les deux profils se croisent souvent, mais
              les stacks et les réflexes diffèrent.
            </p>
          </div>
        </div>
      </section>

      {/* Comparatif */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Comparaison rôles
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            ML Engineer vs AI Engineer vs Data Scientist vs MLOps.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Ces 4 rôles se chevauchent. Mais en 2026, dans une équipe IA
            mature, on les distingue clairement. Voici comment on les
            qualifie sur le terrain.
          </p>
          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-4 py-3">Critère</th>
                    <th className="px-4 py-3">ML Engineer</th>
                    <th className="px-4 py-3">AI Engineer</th>
                    <th className="px-4 py-3">Data Scientist</th>
                    <th className="px-4 py-3">MLOps</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {COMPARATIF.map((r) => (
                    <tr key={r.critere} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-semibold text-slate-900">{r.critere}</td>
                      <td className="px-4 py-3 text-slate-700">{r.mle}</td>
                      <td className="px-4 py-3 text-slate-700">{r.aie}</td>
                      <td className="px-4 py-3 text-slate-700">{r.ds}</td>
                      <td className="px-4 py-3 text-slate-700">{r.mlops}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Stack technique */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Stack technique 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les outils que votre ML Engineer doit maîtriser.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            <StackCard
              Icon={Brain}
              title="Frameworks ML"
              tools={['scikit-learn', 'PyTorch', 'TensorFlow / Keras', 'XGBoost / LightGBM', 'Hugging Face Transformers']}
            />
            <StackCard
              Icon={GitBranch}
              title="Tracking & Versioning"
              tools={['MLflow', 'Weights & Biases', 'DVC', 'Git LFS']}
            />
            <StackCard
              Icon={Cpu}
              title="Plateformes Cloud ML"
              tools={['AWS SageMaker', 'GCP Vertex AI', 'Azure ML', 'Databricks ML']}
            />
            <StackCard
              Icon={Code2}
              title="Orchestration & Pipelines"
              tools={['Airflow', 'Kubeflow Pipelines', 'Prefect', 'Dagster', 'Metaflow']}
            />
            <StackCard
              Icon={TrendingUp}
              title="Feature Store & Monitoring"
              tools={['Feast', 'Tecton', 'Evidently', 'Arize', 'WhyLabs', 'Prometheus + Grafana']}
            />
            <StackCard
              Icon={Cpu}
              title="Infra & Déploiement"
              tools={['Docker', 'Kubernetes', 'Triton Inference Server', 'BentoML', 'Ray Serve']}
            />
          </div>
        </div>
      </section>

      {/* Salaires */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires ML Engineer en PACA — mars 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            42-100k€, +5 à +10% sur Sophia, premium MLOps sur profils GPU.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Le marché ML Engineer est moins tendu que l'AI Engineer pur
            LLM-first, mais les profils <strong>avec expérience MLOps en
            production</strong> sont rares (et chers).
          </p>
          <div className="mt-8">
            <SalaireMlTable />
          </div>
          <p className="mt-6 text-sm text-slate-600">
            Source : compilation Recruteurs.io · Glassdoor · Hellowork ·
            Factoriel · observations terrain mars 2026.
          </p>
        </div>
      </section>

      {/* 5 questions pour démasquer */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Détecter les faux ML Engineers
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            5 questions techniques que nous posons en pré-qualification.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Ces questions ne demandent pas de coder. Elles révèlent en 10
            minutes si le candidat a effectivement mis du ML en production
            ou s'il a juste fait des tutos Kaggle.
          </p>
          <div className="mt-8 space-y-4">
            {QUESTIONS.map((q, i) => (
              <div
                key={q.q}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-slate-900">{q.q}</h3>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Bon signe
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-emerald-900">{q.good}</p>
                      </div>
                      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-700">
                          <XCircle className="h-3.5 w-3.5" />
                          Drapeau rouge
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-rose-900">{q.bad}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tableau salaires PACA */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires IA & ML PACA — tous métiers
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Comparer avec les autres métiers IA en PACA.
          </h2>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
        </div>
      </section>

      <IaPacaCTA
        title="Vous cherchez un ML Engineer en PACA ?"
        subtitle="Notre cabinet a un pipeline actif sur ces profils. Brief 30 min, court-list 4-6 candidats qualifiés en 15-20 jours, focus production."
      />
    </Layout>
  );
}

function StackCard({
  Icon,
  title,
  tools,
}: {
  Icon: typeof Brain;
  title: string;
  tools: string[];
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-violet-600" />
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {tools.map((t) => (
          <span
            key={t}
            className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function SalaireMlTable() {
  const rows = [
    { ville: 'Marseille', junior: '42 – 50 k€', confirme: '52 – 70 k€', senior: '72 – 95 k€', expert: '90 – 120 k€' },
    { ville: 'Aix-en-Provence', junior: '40 – 48 k€', confirme: '50 – 68 k€', senior: '70 – 92 k€', expert: '88 – 118 k€' },
    { ville: 'Sophia Antipolis', junior: '45 – 55 k€', confirme: '55 – 78 k€', senior: '78 – 105 k€', expert: '100 – 130 k€' },
    { ville: 'Nice', junior: '42 – 50 k€', confirme: '52 – 72 k€', senior: '72 – 98 k€', expert: '95 – 125 k€' },
  ];
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3">Ville</th>
              <th className="px-4 py-3">Junior</th>
              <th className="px-4 py-3">Confirmé</th>
              <th className="px-4 py-3">Senior</th>
              <th className="px-4 py-3">Expert / Lead</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.ville} className="hover:bg-slate-50/50">
                <td className="px-4 py-3 font-semibold text-slate-900">{r.ville}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.junior}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.confirme}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.senior}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.expert}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const COMPARATIF = [
  {
    critere: 'Focus stack',
    mle: 'PyTorch, scikit-learn, XGBoost, MLflow',
    aie: 'LangChain, LangGraph, vector DB, LLM APIs',
    ds: 'Pandas, scikit-learn, statsmodels, notebooks',
    mlops: 'Kubernetes, Docker, Terraform, CI/CD ML',
  },
  {
    critere: 'Objectif principal',
    mle: 'Industrialiser des modèles ML en production',
    aie: 'Builder des produits IA LLM (RAG, agents)',
    ds: 'Analyser, modéliser, prototyper',
    mlops: 'Infra & ops des plateformes ML',
  },
  {
    critere: 'Livre des notebooks',
    mle: 'Non — code de production, API, services',
    aie: 'Non — apps, agents, intégrations',
    ds: 'Oui — analyses, POC, dashboards',
    mlops: 'Non — pipelines, infra-as-code',
  },
  {
    critere: 'Connaît Kubernetes',
    mle: 'Oui (basique à avancé)',
    aie: 'Oui (basique)',
    ds: 'Rarement',
    mlops: 'Oui (avancé, c\'est son métier)',
  },
  {
    critere: 'Salaire médian PACA',
    mle: '55-78 k€ confirmé',
    aie: '55-75 k€ confirmé',
    ds: '48-65 k€ confirmé',
    mlops: '55-78 k€ confirmé',
  },
];

const QUESTIONS = [
  {
    q: "Décris ton dernier modèle mis en production. Comment il était déployé, et comment tu détectais qu'il dérivait ?",
    good: "Cite un cas concret : modèle, dataset, infra (SageMaker / Vertex / K8s), monitoring (Evidently, Arize, drift de features, drift de prédictions, métriques business). Sait expliquer ce qui s'est cassé et comment ça a été corrigé.",
    bad: "Reste vague, parle de 'on a déployé en prod' sans détailler comment, n'a jamais regardé son modèle vivre après le go-live, ne sait pas ce qu'est le data drift vs concept drift.",
  },
  {
    q: "Comment tu gères la reproductibilité d'un entraînement entre dev et prod ?",
    good: "Cite des outils concrets : MLflow / W&B pour le tracking, DVC pour les data, seeds fixés, container Docker reproductible, requirements pinned, hash des datasets. Sait expliquer les pièges (non-déterminisme GPU, version PyTorch / CUDA).",
    bad: "'On utilise le même notebook' ou 'Git suffit'. Ne mentionne ni tracking, ni versioning de données, ni containerisation.",
  },
  {
    q: "Tu as un dataset déséquilibré (1% positifs). Tu fais quoi avant même de regarder un modèle ?",
    good: "Définit la métrique métier (precision/recall, F1, AUC-PR vs ROC, coût d'erreur asymétrique). Vérifie la qualité des labels, le leakage, la cohérence train/val/test. Mentionne stratified split, échantillonnage (SMOTE, undersampling) en option, mais surtout métriques calibrées.",
    bad: "Saute direct sur 'on fait du SMOTE' ou 'on entraîne un XGBoost avec class_weight'. Ne parle pas de métrique business, n'évoque pas le leakage.",
  },
  {
    q: "Quelle est la dernière fois où tu as réentraîné un modèle en prod ? Et comment ça s'est déclenché ?",
    good: "Décrit un trigger concret : monitoring drift, baisse de métrique métier, cron périodique, nouveau batch de données labellisées. Cite le process : entraînement, validation A/B ou shadow, rollout progressif, rollback prévu.",
    bad: "'On réentraîne quand on peut' ou 'jamais, ça marche bien'. N'a pas de stratégie de re-training. Pas de A/B testing.",
  },
  {
    q: "On te demande de mettre un modèle XGBoost à 50ms de latence p99 sur 1000 req/s. Comment tu instrumentes ?",
    good: "Pense capacity planning : taille du modèle, batching, vectorisation, ONNX / Treelite pour XGBoost, choix infra (CPU vs GPU selon modèle), autoscaling, monitoring p50/p95/p99. Cite Triton, BentoML, ou un wrapper FastAPI selon contexte.",
    bad: "Répond 'on le met derrière une API FastAPI'. N'a pas de notion de p99, ne pense pas au batching, à la sérialisation, à l'autoscaling.",
  },
];
